import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { LocationFillViewfinderSFSymbolMedium } from '@/assets/sf-symbols/medium/location.fill.viewfinder';
import { cn } from '@/utils/cn';
import type { BrowserElementInspectionPresentation } from '../../types/conversation-message';

function normalized(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : undefined;
}

type CapsuleMetrics = {
  collapsedWidth: number;
  collapsedHeight: number;
  expandedWidth: number;
  expandedHeight: number;
};

type CapsuleStyle = CSSProperties & {
  '--collapsed-width'?: string;
  '--collapsed-height'?: string;
  '--expanded-width'?: string;
  '--expanded-height'?: string;
};

function roundedRectSize(
  element: Element | null
): { width: number; height: number } | undefined {
  if (!element) {
    return undefined;
  }

  const rect = element.getBoundingClientRect();
  return {
    width: Math.ceil(rect.width),
    height: Math.ceil(rect.height),
  };
}

export const ElementInspectionContextView = ({
  messageId,
  presentation,
}: {
  messageId: string;
  presentation: BrowserElementInspectionPresentation;
}) => {
  const userMessage = normalized(presentation.userMessage);
  const measureRef = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState<CapsuleMetrics>();
  const hasScreenshot = normalized(presentation.screenshotDataURL);
  const isScreenshotPending = presentation.screenshotStatus === 'pending';

  useLayoutEffect(() => {
    const measureRoot = measureRef.current;
    if (!measureRoot) {
      return;
    }

    let animationFrame = 0;

    const updateMetrics = () => {
      const collapsed = roundedRectSize(
        measureRoot.querySelector('[data-element-capsule-measure="collapsed"]')
      );
      const expanded = roundedRectSize(
        measureRoot.querySelector('[data-element-capsule-measure="expanded"]')
      );
      if (!collapsed || !expanded) {
        return;
      }

      const nextMetrics: CapsuleMetrics = {
        collapsedWidth: collapsed.width,
        collapsedHeight: collapsed.height,
        expandedWidth: Math.max(collapsed.width, expanded.width),
        expandedHeight: Math.max(collapsed.height, expanded.height),
      };

      setMetrics(current => {
        if (
          current &&
          current.collapsedWidth === nextMetrics.collapsedWidth &&
          current.collapsedHeight === nextMetrics.collapsedHeight &&
          current.expandedWidth === nextMetrics.expandedWidth &&
          current.expandedHeight === nextMetrics.expandedHeight
        ) {
          return current;
        }
        return nextMetrics;
      });
    };

    updateMetrics();
    animationFrame = window.requestAnimationFrame(updateMetrics);

    const resizeObserver = new ResizeObserver(updateMetrics);
    resizeObserver.observe(measureRoot);
    window.addEventListener('resize', updateMetrics);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateMetrics);
    };
  }, [userMessage]);

  const capsuleStyle: CapsuleStyle | undefined = metrics
    ? {
        '--collapsed-width': `${metrics.collapsedWidth}px`,
        '--collapsed-height': `${metrics.collapsedHeight}px`,
        '--expanded-width': `${metrics.expandedWidth}px`,
        '--expanded-height': `${metrics.expandedHeight}px`,
      }
    : undefined;

  return (
    <div className="element-inspection-context relative flex w-fit max-w-full flex-col items-end gap-2">
      {(hasScreenshot || isScreenshotPending) && (
        <div className="w-56 max-w-full overflow-hidden rounded-[1.125rem] border border-border/40 bg-muted/30 shadow-sm">
          {hasScreenshot ? (
            <img
              src={hasScreenshot}
              alt=""
              className="block max-h-40 w-full object-contain"
              draggable={false}
            />
          ) : (
            <div className="h-24 w-full animate-pulse bg-muted/70" />
          )}
        </div>
      )}

      <div
        ref={measureRef}
        className="pointer-events-none absolute right-0 top-0 -z-10 invisible"
        aria-hidden="true"
      >
        <div
          data-element-capsule-measure="collapsed"
          className="inline-flex rounded-[1.125rem] px-3 py-2 text-sm"
        >
          <div className="flex items-center justify-end gap-2 leading-normal">
            <span className="grid size-4 shrink-0 place-items-center">
              <LocationFillViewfinderSFSymbolMedium className="text-sm" />
            </span>
            <span>Element</span>
          </div>
        </div>
        <div
          data-element-capsule-measure="expanded"
          className="mt-4 inline-flex flex-col items-end rounded-[1.125rem] px-3 py-2 text-sm"
        >
          <div className="flex w-full items-center justify-end gap-2 leading-normal">
            <span className="grid size-4 shrink-0 place-items-center">
              <LocationFillViewfinderSFSymbolMedium className="text-sm" />
            </span>
            <span>Element</span>
          </div>
          {userMessage && (
            <div className="mt-2 w-max max-w-[min(26rem,calc(100vw-2rem))] whitespace-pre-wrap wrap-break-word">
              {userMessage}
            </div>
          )}
        </div>
      </div>

      <div className="group/element w-fit max-w-full">
        <div
          style={capsuleStyle}
          className={cn(
            'user-message-bubble',
            'w-fit max-w-full min-w-10 overflow-hidden',
            'bg-user-bubble text-foreground',
            'rounded-[1.125rem] px-3 py-2 text-sm',
            'transition-[width,height,background-color] duration-200 ease-out',
            'group-hover/element:bg-user-bubble-hover',
            metrics &&
              'h-[var(--collapsed-height)] w-[var(--collapsed-width)] group-hover/element:h-[var(--expanded-height)] group-hover/element:w-[var(--expanded-width)]'
          )}
          data-message-id={messageId}
          data-quote-source="true"
        >
          <div className="flex w-full items-center justify-end gap-2 leading-normal">
            <span className="grid size-4 shrink-0 place-items-center text-muted-foreground">
              <LocationFillViewfinderSFSymbolMedium className="text-sm" />
            </span>
            <span>Element</span>
          </div>

          {userMessage && metrics && (
            <div className="mt-2 w-max max-w-[calc(var(--expanded-width)-1.5rem)] opacity-0 transition-opacity duration-150 ease-out group-hover/element:delay-75 group-hover/element:opacity-100">
              <div className="whitespace-pre-wrap wrap-break-word text-left">
                {userMessage}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
